/**
 * Image Cache Service
 * 
 * MVP: Simple sequential preloading (5 ahead + 5 behind)
 * Future: Pluggable strategy pattern for spatial/predictive preloading
 */

import { toBlobUrl } from '@/config'

interface CacheEntry {
  image: HTMLImageElement;
  url: string;
  timestamp: number;
  size: number; // estimated size in bytes
}

interface PreloadStrategy {
  /**
   * Returns array of camera indices to preload, in priority order
   */
  getPrioritizedIndices(
    currentIndex: number,
    totalCameras: number
  ): number[];
}

/**
 * Sequential preloading strategy (MVP)
 * Preloads frames ahead and behind current position
 */
class SequentialStrategy implements PreloadStrategy {
  constructor(
    private readonly ahead: number = 5,
    private readonly behind: number = 5
  ) {}
  
  /**
   * PART 3: Auto-boost buffer for large sections (>50 frames)
   * Increases preload window to 10+10 frames for long sequences
   * This increases preload buffer from 400ms to 800ms at 80ms/frame
   */
  static createForSectionSize(totalCameras: number): SequentialStrategy {
    const bufferSize = totalCameras > 50 ? 10 : 5
    console.log(`📦 [SequentialStrategy] Section size: ${totalCameras} frames → buffer: ${bufferSize}+${bufferSize}`)
    return new SequentialStrategy(bufferSize, bufferSize)
  }

  getPrioritizedIndices(currentIndex: number, totalCameras: number): number[] {
    const indices: number[] = [];
    
    // Alternate between ahead and behind for balanced loading
    for (let i = 1; i <= Math.max(this.ahead, this.behind); i++) {
      // Add frame ahead
      if (i <= this.ahead) {
        const nextIndex = currentIndex + i;
        if (nextIndex < totalCameras) {
          indices.push(nextIndex);
        }
      }
      
      // Add frame behind
      if (i <= this.behind) {
        const prevIndex = currentIndex - i;
        if (prevIndex >= 0) {
          indices.push(prevIndex);
        }
      }
    }
    
    return indices;
  }
}

/**
 * Image Cache with LRU eviction
 */
export class ImageCache {
  private cache = new Map<string, CacheEntry>();
  private accessOrder: string[] = []; // LRU tracking
  
  private readonly maxSizeBytes: number;
  private currentSizeBytes = 0;
  
  private strategy: PreloadStrategy;
  
  constructor(
    maxSizeMB: number = 50,
    strategy?: PreloadStrategy
  ) {
    this.maxSizeBytes = maxSizeMB * 1024 * 1024;
    this.strategy = strategy || new SequentialStrategy(5, 5);
  }
  
  /**
   * Get cached image if available
   */
  get(url: string): HTMLImageElement | null {
    const entry = this.cache.get(url);
    
    if (entry) {
      // Update LRU: move to end (most recently used)
      this.accessOrder = this.accessOrder.filter(u => u !== url);
      this.accessOrder.push(url);
      
      // Removed spam: console.log('✅ ImageCache HIT:', url);
      return entry.image;
    }
    
    return null;
  }
  
  /**
   * PART 2: Check if image is cached without updating LRU
   * Used for animation image-ready checks
   */
  has(url: string): boolean {
    return this.cache.has(url);
  }
  
  /**
   * Add already-loaded image to cache (synchronous)
   * Used when NewModeViewer loads an image and needs to cache it immediately
   */
  addLoadedImage(url: string, img: HTMLImageElement): void {
    // Check if already cached
    if (this.cache.has(url)) {
      return;
    }

    // Estimate image size
    const estimatedSize = img.width * img.height * 4; // RGBA bytes

    const entry: CacheEntry = {
      image: img,
      url,
      timestamp: Date.now(),
      size: estimatedSize,
    };

    // Add to cache
    this.cache.set(url, entry);
    this.accessOrder.push(url);
    this.currentSizeBytes += estimatedSize;

    // Evict if over limit
    this.evictIfNeeded();
    
    console.log(`📸 [ImageCache] Added loaded image: ${url.split('/').pop()}`);
  }

  /**
   * Preload image and add to cache
   */
  async preload(url: string): Promise<HTMLImageElement> {
    // Check if already cached
    const cached = this.get(url);
    if (cached) {
      return cached;
    }
    
    return new Promise((resolve, reject) => {
      const img = new Image();
      
      img.onload = () => {
        // Estimate image size (rough approximation)
        const estimatedSize = img.width * img.height * 4; // RGBA bytes
        
        const entry: CacheEntry = {
          image: img,
          url,
          timestamp: Date.now(),
          size: estimatedSize,
        };
        
        // Add to cache
        this.cache.set(url, entry);
        this.accessOrder.push(url);
        this.currentSizeBytes += estimatedSize;
        
        // Evict if over limit
        this.evictIfNeeded();
        
        resolve(img);
      };
      
      img.onerror = (error) => {
        console.error('❌ ImageCache LOAD ERROR for URL:', url);
        console.error('   Error:', error);
        reject(new Error(`Failed to load image: ${url}`));
      };
      
      img.src = toBlobUrl(url);
    });
  }
  
  /**
   * Preload multiple images based on current position and strategy
   */
  async preloadAround(
    currentIndex: number,
    totalCameras: number,
    getUrlForIndex: (index: number) => string
  ): Promise<void> {
    // CRITICAL: Always load current frame first
    const currentUrl = getUrlForIndex(currentIndex);
    const currentPromise = this.preload(currentUrl).catch(error => {
      console.error(`❌ Failed to preload CURRENT frame ${currentIndex}:`, error);
    });
    
    // Get surrounding frames from strategy
    const indicesToLoad = this.strategy.getPrioritizedIndices(
      currentIndex,
      totalCameras
    );
    
    // Preload surrounding frames in priority order (don't wait for each to complete)
    const surroundingPromises = indicesToLoad.map(index => {
      const url = getUrlForIndex(index);
      return this.preload(url).catch(error => {
        // Don't fail entire preload if one image fails
        console.warn(`Failed to preload frame ${index}:`, error);
      });
    });
    
    // Wait for CURRENT frame first, then surrounding frames
    await currentPromise;
    await Promise.allSettled(surroundingPromises);
  }
  
  /**
   * Evict least recently used images until under size limit
   */
  private evictIfNeeded(): void {
    while (this.currentSizeBytes > this.maxSizeBytes && this.accessOrder.length > 0) {
      // Remove least recently used (first in array)
      const urlToEvict = this.accessOrder.shift();
      if (!urlToEvict) break;
      
      const entry = this.cache.get(urlToEvict);
      if (entry) {
        this.currentSizeBytes -= entry.size;
        this.cache.delete(urlToEvict);
        // Eviction occurs silently - only log errors if needed
      }
    }
  }
  
  /**
   * Clear entire cache
   */
  clear(): void {
    console.log('🗑️ ImageCache CLEARED', {
      entriesCleared: this.cache.size,
      bytesFreed: this.currentSizeBytes,
    });
    
    this.cache.clear();
    this.accessOrder = [];
    this.currentSizeBytes = 0;
  }
  
  /**
   * Get cache statistics
   */
  getStats() {
    return {
      entries: this.cache.size,
      sizeMB: (this.currentSizeBytes / 1024 / 1024).toFixed(2),
      maxSizeMB: (this.maxSizeBytes / 1024 / 1024).toFixed(2),
      utilizationPercent: ((this.currentSizeBytes / this.maxSizeBytes) * 100).toFixed(1),
    };
  }
  
  /**
   * Change preloading strategy (for Phase 2)
   */
  setStrategy(strategy: PreloadStrategy): void {
    console.log('🔄 ImageCache: Strategy changed to', strategy.constructor.name);
    this.strategy = strategy;
  }
}

// Export singleton instance
export const imageCache = new ImageCache(50); // 50MB default

// Export strategy class for Phase 2 extensions
export type { PreloadStrategy };
export { SequentialStrategy };
